import { ShiftManagementScheduleBulkCopyDocument } from 'generated/resources/graphql'
import uniq from 'lodash/uniq'

import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { LOGIC_OPERATOR } from 'modules/shift_management/hooks/reducers/filter'
import { useRefreshStats } from 'modules/shift_management/hooks/refreshShiftsRows'
import { useFilters } from 'modules/shift_management/hooks/useFilters'
import { usePagination } from 'modules/shift_management/hooks/usePagination'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'
import { useUI } from 'modules/shift_management/hooks/useUI'
import { nameFromHexCode } from 'modules/shift_management/lib/colors'
import { trackAddShiftSubmitEvent } from 'modules/shift_management/lib/trackEvents'
import Shift from 'modules/shift_management/store/internal/models/Shift'
import type View from 'modules/shift_management/store/internal/models/View'
import { updateCreatedRestDaysCache } from 'modules/shift_management/utils/apolloCache/updateCreatedRestDaysCache'
import { updateCreatedShiftsCache } from 'modules/shift_management/utils/apolloCache/updateCreatedShiftsCache'

interface Props {
  originalView: View
  targetView: View
}

const useCopySchedule = ({ originalView, targetView }: Props) => {
  const [{ shiftsRows }] = useShiftsRows()
  const [{ view }, dispatchSchedule] = useSchedule()
  const [, dispatchPagination] = usePagination()
  const [, dispatchUI] = useUI()

  const { refreshStats } = useRefreshStats()
  const {
    company: { id: currentCompanyId },
  } = useCurrentSession()
  const [{ employeeIds, teamIds, legalEntityIds, customFilters }] = useFilters()

  const [bulkCopySchedule] = useMutation(ShiftManagementScheduleBulkCopyDocument, {
    extractorFn: (x) => x.shiftManagementMutations.bulkCopyShiftmanagementSchedule,
    update: (cache, { data: mutationData }) => {
      const schedule =
        mutationData?.shiftManagementMutations.bulkCopyShiftmanagementSchedule.schedule
      const shifts = schedule?.shifts || []
      const restDays = schedule?.restDays || []

      updateCreatedShiftsCache({
        cache,
        view: targetView,
        shifts,
      })

      updateCreatedRestDaysCache({
        cache,
        view: targetView,
        restDays,
      })
    },
  })

  const { running: submitting, performAction: onConfirm } = useAsyncAction(
    async (closePortal: ClosePortalFn) => {
      const employees = employeeIds?.length ? employeeIds : []

      const mutationResult = await bulkCopySchedule({
        variables: {
          companyId: currentCompanyId,
          originalStartAt: originalView.startAt,
          targetStartAt: targetView.startAt,
          employeeIds: employees,
          logicOperator: LOGIC_OPERATOR.AND,
          customFilters,
          includeDraftShifts: true,
          teamIds,
          legalEntityIds,
        },
      })

      const handled = handleResult(mutationResult, (_errors) => {})

      if (handled.success) {
        const newShifts = handled.data?.schedule?.shifts || []
        const copiedShifts = newShifts.map((shift) =>
          Shift.buildFromGraphQL({
            shift,
            employeeFullName: shift?.employee?.fullName || '',
            employeeAvatar: shift?.employee?.avatar?.url || '',
            employeeLocationId: shift?.employee?.locationId || '0',
          })
        )

        const impactedEmployees: string[] = uniq(copiedShifts.map((shift) => shift.employeeId))
        const hexCodes = copiedShifts.map((shift) => shift.color)
        const uniqHexCodes = uniq(hexCodes)
        const color = uniqHexCodes.map((code) => (code ? nameFromHexCode(code) : null))

        trackAddShiftSubmitEvent({
          view,
          employeeIds: impactedEmployees,
          shiftsCount: copiedShifts.length,
          restDaysCount: handled.data?.schedule?.restDays?.length || 0,
          shiftType: 'copyPeriod',
          color,
          from: 'copy_period',
        })

        dispatchUI({ type: 'LOADING' })
        dispatchSchedule({ type: 'RESET_VIEW', view })
        dispatchPagination({ type: 'RESET_PAGINATION' })

        await refreshStats(shiftsRows)
      }

      closePortal()
    },
    {
      successMessage: i18n.t('shift_management.actions.copy_shifts_success'),
      errorFlash: false,
    }
  )

  return {
    submitting,
    onConfirm,
  }
}

export default useCopySchedule
